import { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import PaginationControls from "../../Analysis/ComponentsAnalysis/PaginationControls/PaginationControls";
import SearchExport from "../../Analysis/ComponentsAnalysis/SearchExport/SearchExport";
import FilterSectionExtract from "../FilterSectionExtract/FilterSectionExtract";
import ExtractSindicatoTable from "./ExtractSindicatoTable";
import { mapSolicitacoesToSindicatoRows } from "./mapSolicitacoesToSindicatoRows";
import { fetchExtratoSindicatoListagem } from "./extractSindicatoService";
import MockModeBanner from "./MockModeBanner";
import {
  getExtratoSolicitacoesByAno,
  getExtratoSolicitacoesLegacy,
} from "../../../../services/ExtractService";
import { formatDateTimeExtratoList } from "../../../../utils/date";
import type { ExtractRow } from "../shared/extractListTypes";
import {
  onlyDigits,
  rowSituacaoExtratoMatchesFilters,
} from "../shared/extractListUtils";
import { getAnoRequestParam } from "../V2/anoRequestParam";
import "../Extract.css";
import "./ExtractSindicatoDetalhe.css";
import "../../Analysis/ComponentsAnalysis/PaginationControls/PaginationControls.css";

const STORAGE_KEY = "extract_sindicato_filters_v1";

const ALL_SITUATIONS = [
  "Análise não finalizada",
  "Extrato Pendente",
  "Extrato Enviado",
  "Extrato Recusado",
  "Extrato Validado",
  "Inapto",
];

type SortConfig = {
  key: string | null;
  direction: "asc" | "desc" | null;
};

const ExtractSindicato = () => {
  const [data, setData] = useState<ExtractRow[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([2026]);
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });
  const [selectedSituation, setSelectedSituation] =
    useState<string[]>(ALL_SITUATIONS);
  const [searchTerm, setSearchTerm] = useState("");
  const skipNextYearFetchRef = useRef(false);

  const extractAnosModelo = (
    response:
      | {
          avaliacoesModelo?: { anoReferencia: number }[];
          extratosModelo?: { anoReferencia: number }[];
        }
      | null
      | undefined,
  ): number[] => {
    const anos =
      response?.avaliacoesModelo?.map((m) => m.anoReferencia) ??
      response?.extratosModelo?.map((m) => m.anoReferencia) ??
      [];
    return [...new Set(anos)].sort((a, b) => b - a);
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.selectedSituation)) {
        const stored = parsed.selectedSituation.filter(
          (s: unknown) => typeof s === "string",
        ) as string[];
        const sanitized = stored.filter((s) => ALL_SITUATIONS.includes(s));
        const newlyAdded = ALL_SITUATIONS.filter((s) => !stored.includes(s));
        const result = [...sanitized, ...newlyAdded];
        setSelectedSituation(result.length > 0 ? result : ALL_SITUATIONS);
      }
      if (typeof parsed?.searchTerm === "string") {
        setSearchTerm(parsed.searchTerm);
      }
      if (typeof parsed?.selectedYear === "number") {
        setSelectedYear(parsed.selectedYear);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ selectedSituation, searchTerm, selectedYear }),
      );
    } catch {
      /* ignore */
    }
  }, [selectedSituation, searchTerm, selectedYear]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  const loadRowsForYear = async (ano: number) => {
    const mockListagem = await fetchExtratoSindicatoListagem();
    if (mockListagem) {
      return mapSolicitacoesToSindicatoRows(mockListagem, ano);
    }

    if (ano === 2025) {
      const extratoResponse = await getExtratoSolicitacoesLegacy();
      const solicitacoes = extratoResponse?.solicitacoes ?? [];
      return mapSolicitacoesToSindicatoRows(solicitacoes, ano);
    }

    const extratoResponse = await getExtratoSolicitacoesByAno(
      getAnoRequestParam(ano),
    );
    const solicitacoes = extratoResponse?.solicitacoes ?? [];
    const anosModelo = extractAnosModelo(extratoResponse).filter(
      (ano) => ano !== 2025,
    );
    if (anosModelo.length > 0) {
      setAvailableYears(anosModelo);
    }

    return mapSolicitacoesToSindicatoRows(solicitacoes, ano);
  };

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      try {
        const rows = await loadRowsForYear(selectedYear);
        setData(rows);
      } catch (error) {
        console.error("Erro ao carregar extratos do sindicato:", error);
        setData([]);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    if (skipNextYearFetchRef.current) {
      skipNextYearFetchRef.current = false;
      return;
    }

    fetchRows();
  }, [selectedYear]);

  const handleSituationFilter = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setSelectedSituation(value);
      return;
    }
    setSelectedSituation((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value],
    );
    setCurrentPage(1);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        const direction =
          prev.direction === "asc"
            ? "desc"
            : prev.direction === "desc"
              ? null
              : "asc";
        return { key: direction ? key : null, direction };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const termDigits = onlyDigits(searchTerm);

    return data.filter((row) => {
      if (
        !rowSituacaoExtratoMatchesFilters(
          row.situacao,
          selectedSituation,
          ALL_SITUATIONS,
        )
      ) {
        return false;
      }

      if (!term) return true;

      const solicitacao = String(row.solicitacao || "").toLowerCase();
      const sigla = String(row.sigla || "").toLowerCase();
      const cnpj = onlyDigits(row.cnpj);

      return (
        solicitacao.includes(term) ||
        sigla.includes(term) ||
        (termDigits.length > 0 && cnpj.includes(termDigits))
      );
    });
  }, [data, searchTerm, selectedSituation]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;

    const key = sortConfig.key;
    const dir = sortConfig.direction === "asc" ? 1 : -1;

    return [...filteredData].sort((a, b) => {
      const av = (a as Record<string, unknown>)[key];
      const bv = (b as Record<string, unknown>)[key];

      if (key === "atualizacao" || key === "envio") {
        const at = av ? new Date(String(av)).getTime() : 0;
        const bt = bv ? new Date(String(bv)).getTime() : 0;
        return (at - bt) * dir;
      }

      return String(av ?? "").localeCompare(String(bv ?? ""), "pt-BR") * dir;
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));
  const startResult =
    sortedData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, sortedData.length);
  const currentItems = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const exportData = useMemo(
    () =>
      sortedData.map((item) => ({
        SOLICITACAO: item.solicitacao ?? "",
        SITUACAO: item.situacao ?? "",
        ENVIO_EXTRATO: formatDateTimeExtratoList(item.envio),
        ULTIMA_ATUALIZACAO: formatDateTimeExtratoList(item.atualizacao),
      })),
    [sortedData],
  );

  if (initialLoading || loading) {
    return <PageLoading />;
  }

  return (
    <Container fluid className="extract-container">
      <MockModeBanner />
      <h1 className="extract-title">GESTÃO DE EXTRATOS</h1>

      <div className="filters-actions-container shipping-filters-actions-container">
        <FilterSectionExtract
          selectedSituation={selectedSituation}
          handleSituationFilter={handleSituationFilter}
          allSituations={ALL_SITUATIONS}
        />

        <SearchExport
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          availableYears={availableYears}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          exportData={exportData}
          fileName={`GestaoExtratosSindicato_${selectedYear}`}
        />
      </div>

      <div className="table-responsive-wrapper extract-table-scroll">
        <ExtractSindicatoTable
          currentItems={currentItems}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
          selectedYear={selectedYear}
          formatDate={formatDateTimeExtratoList}
        />
      </div>

      <div className="pagination-wrapper">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          startResult={startResult}
          endResult={endResult}
          totalItems={sortedData.length}
        />
      </div>
    </Container>
  );
};

export default ExtractSindicato;
